import { useAuth } from "providers/AuthProvider";

import {
  getSignInViaEmailLinkLocalStorageData,
  removeSignInViaEmailLinkLocalStorage
} from "services/firebase/auth";

import { useTypedState } from "seneca-common/utils/routing/react-router-typesafe-routes/dom";

import useUpdatePreferences from "features/notifications/hooks/useUpdatePreferences";
import { linkToParentDashboard } from "features/parents/links";
import { PreconnectedParentSignInData } from "features/passwordless-sign-in/parent/types";
import { analyticsLogPreferencesChangeOnSignUp } from "features/settings/features/notifications/analytics";
import { ROUTES } from "routing/routes";

import { useParentDataPrefill } from "./hooks";
import { SignUpCard, SignUpHeading } from "./Layout";
import { useLocationContext } from "./LocationProvider";
import SignUpUserDetailsForm from "./SignUpParentUserDetailsForm";

export default function SignUpParentUserDetails() {
  const { mutateAsync: updateEmailPreferences } = useUpdatePreferences("email");
  const { mutateAsync: updatePushPreferences } = useUpdatePreferences("push");
  const optimisticallyUpdatePreferences = (parentMarketing: boolean) => {
    updateEmailPreferences({ parentMarketing });
    updatePushPreferences({ parentMarketing });
  };

  const { navigateInContext } = useLocationContext();
  const finishSignUpJourney = () => {
    removeSignInViaEmailLinkLocalStorage();

    navigateInContext(linkToParentDashboard(), { replace: true });
  };

  const afterSubmit = ({
    isSubscribedToMarketingEmails: parentMarketing
  }: {
    isSubscribedToMarketingEmails?: boolean;
  }) => {
    if (parentMarketing !== undefined) {
      optimisticallyUpdatePreferences(parentMarketing);
      analyticsLogPreferencesChangeOnSignUp({
        field: "parentMarketing",
        checked: parentMarketing
      });
    }
    finishSignUpJourney();
  };

  const { provider } = useAuth();
  const { showMarketingEmailsCheckbox: showMarketingEmailsCheckboxState } =
    useTypedState(ROUTES.SIGN_UP_PARENT.USER_DETAILS);

  const showMarketingEmailsCheckbox =
    showMarketingEmailsCheckboxState || provider !== "password";

  const { parentData } = useParentDataPrefill();
  const signInViaEmailLinkData =
    getSignInViaEmailLinkLocalStorageData<PreconnectedParentSignInData>();

  const { givenName, familyName } = parentData ?? signInViaEmailLinkData ?? {};

  return (
    <SignUpCard>
      <SignUpHeading>Confirm your details</SignUpHeading>
      <SignUpUserDetailsForm
        showMarketingEmailsCheckbox={showMarketingEmailsCheckbox}
        afterSubmit={afterSubmit}
        givenName={givenName}
        familyName={familyName}
      />
    </SignUpCard>
  );
}
